/**
 * ARTICLE D'EXEMPLE — généré le 2026-09-06 pour valider la maquette du blog.
 * À relire, compléter ou remplacer par du contenu réel avant/après mise en ligne.
 * Sources : 6 URL vérifiées automatiquement (6 OK).
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "besancon-terrain-naturel-ia-sante-chu-isifc-temis",
  "title": "Besançon, terrain naturel pour l’IA en santé : CHU, ISIFC et TEMIS Santé",
  "excerpt": "Hôpital universitaire, école d’ingénieurs du dispositif médical, biocluster TEMIS Santé et microrobotique : pourquoi Besançon réunit les ingrédients de l’IA appliquée à la santé.",
  "seoTitle": "IA en santé : le CHU, l’ISIFC et TEMIS Santé",
  "seoDescription": "Le CHU et son entrepôt de données de santé, l’école d’ingénieurs ISIFC, le biocluster TEMIS Santé et la microrobotique : les bases de l’IA médicale bisontine.",
  "category": "ia",
  "tags": [
    "IA en santé",
    "CHU Besançon",
    "ISIFC",
    "TEMIS Santé",
    "Dispositifs médicaux"
  ],
  "author": "jimmy",
  "publishedAt": "2026-08-07",
  "updatedAt": "2026-08-07",
  "featured": false,
  "cover": {
    "image": "/img/blog/unsplash-1576091160399-112ba8d25d1d.jpg",
    "credit": "Photo : Unsplash",
    "alt": "Plateau technique de recherche biomédicale à Besançon : une chercheuse manipule un microscope à côté d’écrans affichant des images médicales"
  },
  "content": [
    {
      "type": "lead",
      "text": "Ville de microtechniques et d’horlogerie, Besançon est aussi un pôle biomédical complet : un hôpital universitaire, une école d’ingénieurs dédiée au dispositif médical, un biocluster et des laboratoires de microrobotique. Pour l’intelligence artificielle en santé, qui a besoin de données, de capteurs et de cliniciens, la combinaison est rare."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Hôpital",
          "value": "CHU Besançon Franche-Comté, avec un centre d’investigation clinique labellisé Inserm"
        },
        {
          "label": "École",
          "value": "ISIFC, école d’ingénieurs publique spécialisée dans le dispositif médical"
        },
        {
          "label": "Biocluster",
          "value": "TEMIS Santé et son centre Bio Innovation, quartier des Hauts-du-Chazal"
        },
        {
          "label": "Laboratoires",
          "value": "FEMTO-ST (microrobotique biomédicale) et UMR RIGHT (immunothérapie)"
        },
        {
          "label": "Fil rouge",
          "value": "Les microtechniques appliquées au vivant"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Un hôpital universitaire qui structure ses données"
    },
    {
      "type": "p",
      "text": "Le CHU Besançon Franche-Comté abrite un centre d’investigation clinique labellisé Inserm (CIC 1431), qui comprend une plateforme d’évaluation clinique des dispositifs médicaux, DinaMic, une unité de méthodologie et une plateforme de neuroimagerie. Parmi ses plateformes thématiques figurent aussi une structure de bioinformatique et de big data, une plateforme de télémédecine et une unité de modélisation et d’impression 3D.[^1]"
    },
    {
      "type": "p",
      "text": "L’établissement a annoncé le lancement de son propre entrepôt de données de santé, présenté comme une étape clé de sa stratégie de recherche, et participe à un réseau interrégional de CHU qui mutualise le développement de ces entrepôts.[^1] Un entrepôt de données de santé est l’infrastructure qui permet de réutiliser, dans un cadre réglementé, les données produites par le soin (comptes rendus, imagerie, biologie) à des fins de recherche. Sans lui, pas d’apprentissage automatique à l’échelle d’un hôpital."
    },
    {
      "type": "p",
      "text": "Des projets existent déjà. Le CHU a pris part au projet SAIAD, qui automatise par apprentissage profond la segmentation d’images de tumeurs rénales de l’enfant afin de préparer l’intervention chirurgicale, avec l’institut [FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques) parmi les partenaires. Avec le projet MASSAI, il s’intéresse aussi aux enjeux éthiques de l’IA au bloc opératoire.[^1]"
    },
    {
      "type": "h2",
      "text": "L’ISIFC, une école pensée pour le dispositif médical"
    },
    {
      "type": "p",
      "text": "Créé en 2001, l’[Institut supérieur d’ingénieurs de Franche-Comté](https://isifc.univ-fcomte.fr/) (ISIFC) est une école d’ingénieurs publique habilitée par la CTI, qui se présente comme « l’école d’ingénieurs spécialiste du dispositif médical ».[^2] Elle est une composante de l’Université Marie et Louis Pasteur, le nom que porte l’Université de Franche-Comté depuis janvier 2025.[^6]"
    },
    {
      "type": "p",
      "text": "Sa formation repose sur une triple culture, technique, réglementaire et médicale. En troisième année, les élèves choisissent une option parmi biomécanique et microsystèmes, bio-ingénierie, e-santé ou microrobotique pour la santé.[^2] L’option e-santé place le logiciel et la donnée au cœur du dispositif médical, là où l’IA entre en jeu. Or un logiciel destiné au diagnostic ou au traitement relève, en Europe, de la réglementation des dispositifs médicaux, avec les obligations qui vont avec : ce sont précisément les compétences qui manquent souvent aux équipes techniques."
    },
    {
      "type": "h2",
      "text": "TEMIS Santé, là où laboratoires et entreprises se côtoient"
    },
    {
      "type": "p",
      "text": "La [technopole TEMIS](/blog/temis-technopole-startups-deeptech-besancon), historiquement tournée vers les microtechniques, a développé un second site, [TEMIS Santé](https://www.temis.org/), dans le quartier des Hauts-du-Chazal, à proximité immédiate de l’hôpital. Il se présente comme un biocluster consacré aux thérapies innovantes, en particulier aux biothérapies et à la bioproduction.[^3]"
    },
    {
      "type": "p",
      "text": "Le bâtiment Bio Innovation, inauguré en 2021 et fort de près de 4 000 m², héberge des start-up et des projets en maturation, avec des plateaux techniques orientés dispositifs médicaux, diagnostic et biothérapies.[^5] Autour de lui :"
    },
    {
      "type": "ul",
      "items": [
        "l’Établissement français du sang et son pôle d’innovation en biothérapie ;",
        "l’UMR RIGHT (Inserm, université, EFS), qui travaille sur l’immunothérapie des cancers, la transplantation et les maladies inflammatoires, en lien étroit avec le CHU[^6] ;",
        "la faculté des sciences de la santé de l’université ;",
        "des prestataires spécialisés (CRO et CDMO) qui accompagnent essais et production.[^3]"
      ]
    },
    {
      "type": "h2",
      "text": "Les microtechniques appliquées au vivant"
    },
    {
      "type": "p",
      "text": "Le fil rouge reste la microtechnique. L’institut FEMTO-ST, unité mixte du CNRS, de l’Université Marie et Louis Pasteur, de SUPMICROTECH-ENSMM et de l’UTBM, compte sept départements, dont l’automatique et la robotique (AS2M), l’informatique (DISC) et les micro et nanosciences (MN2S).[^4]"
    },
    {
      "type": "p",
      "text": "Au sein d’AS2M, les travaux de microrobotique biomédicale, portés historiquement par l’équipe MiNaRoB (le département a depuis réorganisé ses équipes), couvrent notamment : tri cellulaire microrobotisé pour de nouveaux traitements anticancéreux, microchirurgie laser des cordes vocales, manipulation magnétique de capsules endoscopiques, endoscopie interventionnelle robotisée.[^4] Ces systèmes ont un point commun : ils produisent et consomment des flux d’images et de signaux à interpréter en temps réel, un terrain naturel pour l’apprentissage automatique."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour l’IA",
      "text": "Un modèle d’IA en santé a besoin de trois choses que Besançon réunit sur quelques kilomètres carrés : des données cliniques structurées (l’entrepôt et le centre d’investigation clinique du CHU), des dispositifs qui génèrent des signaux (microrobots, capteurs, imagerie) et des personnes capables de faire le lien entre l’algorithme et la réglementation (les ingénieurs formés à l’[ISIFC](/blog/isifc-nouveau-batiment-inaugure-2025)). C’est cette boucle complète, de la mesure à la validation clinique, qui distingue le territoire d’un simple pôle de data science."
    },
    {
      "type": "h2",
      "text": "Un terrain favorable, pas un acquis"
    },
    {
      "type": "p",
      "text": "Il faut rester mesuré. L’entrepôt de données du CHU est annoncé, pas encore exploité à grande échelle ; les projets d’IA cités restent des projets de recherche, et le passage d’un prototype à un dispositif marqué CE prend des années. Besançon n’est pas non plus le plus gros écosystème de santé français en volume."
    },
    {
      "type": "p",
      "text": "Mais l’essentiel est là : un hôpital qui structure ses données, une école qui forme aux contraintes du médical, des laboratoires qui inventent les instruments et des lieux où tout ce monde se croise. Pour des développeurs qui veulent faire de l’IA appliquée avec des cliniciens à portée de main, c’est un point de départ solide."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "CHU Besançon Franche-Comté",
      "publisher": "CHU Besançon Franche-Comté",
      "url": "https://www.chu-besancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 2,
      "title": "ISIFC Génie Biomédical – Formation d’ingénieur",
      "publisher": "ISIFC / Université Marie et Louis Pasteur",
      "url": "https://isifc.univ-fcomte.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 3,
      "title": "TEMIS Technopole Besançon",
      "publisher": "TEMIS",
      "url": "https://www.temis.org/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 4,
      "title": "Institut FEMTO-ST",
      "publisher": "FEMTO-ST / CNRS",
      "url": "https://www.femto-st.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 5,
      "title": "Grand Besançon Métropole et Ville de Besançon",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 6,
      "title": "Université Marie & Louis Pasteur",
      "publisher": "Université Marie et Louis Pasteur",
      "url": "https://www.umlp.fr/",
      "type": "official",
      "date": "2026-09-06"
    }
  ]
};
